import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { authApi } from '../services/api';

// ── Auth store ────────────────────────────────────────────────────────────────
export const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,

      login: async (email, password) => {
        const { data } = await api.post('/auth/login', {
          email,
          password,
        });

        set({
          user: data.user,
          token: data.token,
          isAuthenticated: true,
        });

        return data;
      },

      // OTP login — step 1: request a code be sent to the given email.
      sendOtp: async (email) => {
        const { data } = await authApi.sendOtp({ email });
        return data;
      },

      // OTP login — step 2: verify the code, complete sign-in.
      verifyOtp: async (email, otp) => {
        const { data } = await authApi.verifyOtp({ email, otp });

        set({
          user: data.user,
          token: data.token,
          isAuthenticated: true,
        });

        return data;
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      // Re-fetches the current user (including role) from the backend and
      // overwrites the persisted copy in localStorage. This is the fix for
      // a stale-role bug: `user` (and its `role`) is cached in
      // localStorage at login time and never updated again on its own, so
      // if an admin changes a user's role on the backend afterward, that
      // user's already-open session kept showing/using the OLD role
      // (e.g. a user promoted to admin wouldn't see admin-only UI like
      // the Users module until they logged out and back in — or, just as
      // bad, a demoted admin would keep seeing admin UI). Call this on
      // app load and on window focus so role changes take effect without
      // requiring a fresh login.
      refreshUser: async () => {
        // GET /auth/me responds with { success, user: { id, email, name,
        // role, isActive, createdAt } } — read that directly, falling
        // back to other shapes only in case the backend contract changes.
        const { data } = await authApi.me();
        const freshUser = data?.user || data?.data || data;
        if (freshUser) {
          set({ user: freshUser });
        }
        return freshUser;
      },
    }),
    {
      name: 'migratepro-auth',

      partialize: (s) => ({
        user: s.user,
        token: s.token,
        isAuthenticated: s.isAuthenticated,
      }),

      onRehydrateStorage: () => (state) => {
        // optional debug only
        // console.log('rehydrated:', state?.token);
      },
    }
  )
);
// ── App / migration store ─────────────────────────────────────────────────────
export const useAppStore = create((set, get) => ({
  // Domo credentials (session only — never persisted)
  domoCredentials: null,
  pbiCredentials:  null,

  // Current migration wizard state
  migrationConfig: {
    domoClientId:   'xxx',
    domoSecret:     'xx',
    domoInstance:   'xxx',
    datasetId:      '',
    pageId:         '',
    pbiTenantId:    'xx',
    pbiAppId:       'xx',
    pbiAppSecret:   'xx',
    workspaceId:    'xx',
    datasourceId:   '',
    datasourceName: '',
    reportName:     '',
    datasetName:    '',
    autoConvertDAX: true,
    autoMapVisuals: true,
    pushHistorical: true,
  },

  // Active job
  activeJobId: null,
  jobProgress: { stage: null, percent: 0, logs: [] },

  // Actions
  setMigrationConfig: (updates) =>
    set(s => ({ migrationConfig: { ...s.migrationConfig, ...updates } })),

  setDomoCredentials: (creds) => set({ domoCredentials: creds }),
  setPbiCredentials:  (creds) => set({ pbiCredentials: creds }),
  setActiveJobId:     (id)    => set({ activeJobId: id }),

  appendLog: (log) =>
    set(s => ({
      jobProgress: {
        ...s.jobProgress,
        logs: [...s.jobProgress.logs.slice(-200), { ...log, ts: new Date().toISOString() }],
      },
    })),

  updateProgress: (update) =>
    set(s => ({
      jobProgress: {
        ...s.jobProgress,
        ...update,
        logs: update.message
          ? [...s.jobProgress.logs.slice(-200), { level: update.level || 'info', message: update.message, ts: new Date().toISOString() }]
          : s.jobProgress.logs,
      },
    })),

  resetProgress: () =>
    set({ jobProgress: { stage: null, percent: 0, logs: [] }, activeJobId: null }),
}));
